import React from 'react';
import { FixedSizeList as List } from 'react-window';
import RowRenderer from './RowRenderer';
import ScoresModal from './ScoresModal';

/**
 * Mobile-optimized table view with virtualization
 * 
 * @param {Object} props - Component props
 * @param {Array} props.filteredData - Array of filtered data items 
 * @param {Array} props.visibleModels - Array of currently visible models
 * @param {Array} props.modelColumns - Array of all model objects
 * @param {Function} props.openModal - Function to open the model scores modal
 * @param {boolean} props.isModalOpen - Whether the modal is open
 * @param {Object} props.selectedItem - Selected item to display in modal
 * @param {Function} props.closeModal - Function to close the modal
 * @param {Object} props.averageScores - Object containing average scores
 */
const MobileTableView = ({ 
  filteredData,
  visibleModels,
  modelColumns,
  openModal,
  isModalOpen,
  selectedItem,
  closeModal,
  averageScores
}) => {
  // Helper function to determine if a model is one of the company's ASI models
  const isCompanyModel = (modelId) => {
    return modelId === 'asi1_mini' || modelId === 'asi1_mini_0225';
  };
  // Use fixed row height for all rows
  const rowHeight = 60;

  // Get average score for the currently visible model
  const averageScore = Math.round(Number(averageScores[visibleModels[0].id]));
  const scoreClass = averageScore >= 85 ? 'text-blue-700' : 'text-blue-500';
  
  const currentModelIsCompany = isCompanyModel(visibleModels[0].id);

  return (
    <div className="border rounded overflow-hidden">
      {/* Average score display - above header */}
      <div className={`p-3 ${currentModelIsCompany ? 'bg-blue-100 border-x-2 border-t-2 border-blue-400' : 'bg-blue-50'} border-b`}>
        <div className="flex justify-between items-center">
          <span className={`text-sm font-medium ${currentModelIsCompany ? 'text-blue-800' : ''}`}>
            Average
          </span>
          <span className={`text-base font-bold ${currentModelIsCompany ? 'text-blue-800' : scoreClass}`}>
            {averageScore}%
          </span>
        </div>
      </div>
      
      {/* Table header */}
      <div className={`${currentModelIsCompany ? 'bg-blue-50 border-x-2 border-blue-400' : 'bg-gray-100'} p-3 flex`}>
        <div className="w-8 text-xs font-medium text-gray-600 uppercase">#</div>
        <div className="flex-1 px-2 text-xs font-medium text-gray-600 uppercase">Task</div>
        <div className="w-12 text-center text-xs font-medium text-gray-600 uppercase">Score</div>
        <div className="w-6"></div>
      </div>
      
      {/* Virtualized list */}
      <div className={`h-[70vh] ${currentModelIsCompany ? 'border-x-2 border-blue-400' : ''}`}>
        <List
          height={window.innerHeight * 0.7}
          itemCount={filteredData.length}
          itemSize={rowHeight}
          width="100%"
          overscanCount={5}
        >
          {({ index, style }) => {
            const item = filteredData[index];
            
            return (
              <RowRenderer
                item={item}
                style={style}
                index={index}
                openModal={openModal}
                modelColumns={modelColumns}
                visibleModels={visibleModels}
                isMobileView={true}
              />
            );
          }}
        </List>
      </div>
      
      {/* Footer note */}
      <div className={`mt-2 text-xs text-center ${currentModelIsCompany ? 'text-blue-700 border-b-2 border-x-2 border-blue-400 bg-blue-50 p-2' : ''}`}>
        Tap on <span className="inline-block w-4 h-4 rounded-full border border-blue-300 bg-blue-50 text-blue-600 text-xs leading-4 align-text-bottom mx-1">i</span> to see all model scores
      </div>

      {/* Model scores modal */}
      <ScoresModal 
        item={selectedItem}
        modelColumns={modelColumns}
        isOpen={isModalOpen}
        onClose={closeModal}
      />
    </div>
  );
};

export default MobileTableView;
